import React, {useEffect, useState} from "react";
import api from "../api/axiosInstance";

const PromotionsAdminPage = () => {
    const [promotions, setPromotions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showModal, setShowModal] = useState(false);

    const [editId, setEditId] = useState(null);
    const [formData, setFormData] = useState({
        title: "",
        description: "",
        type: "BOGO_PERCENT",
        discountValue: "",
        discountPercent: 0,
        discountAmount: 0,
        maxDiscount: 0,
        skuList: "",
        startDate: "",
        endDate: "",
    });

    useEffect(() => {
        fetchPromotions();
    }, []);

    const fetchPromotions = async () => {
        try {
            const res = await api.get("/admin/promotions");
            setPromotions(res.data || []);
        } catch (err) {
            setError(err.response?.data?.error || err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (e) => {
        const { name, value, type } = e.target;

        setFormData(prev => ({
            ...prev,
            [name]: type === "number" ? Number(value) : value
        }));
    };

    const openCreateModal = () => {
        setEditId(null);
        setFormData({
            title: "",
            description: "",
            type: "BOGO_PERCENT",
            discountValue: "",
            discountPercent: 0,
            buyQuantity: 0,
            getQuantity: 0,
            discountAmount: 0,
            maxDiscount: 0,
            skuList: "",
            startDate: "",
            endDate: "",
        });
        setShowModal(true);
    };

    const openEditModal = (promo) => {
        setEditId(promo._id);
        setFormData({
            title: promo.title || "",
            description: promo.description || "",
            type: promo.type || "BOGO_PERCENT",
            discountValue: promo.discountValue || 0,
            discountPercent: promo.discountPercent || 0,
            buyQuantity: promo.buyQuantity || 0,
            getQuantity: promo.getQuantity || 0,
            discountAmount: promo.discountAmount || 0,
            maxDiscount: promo.maxDiscount || 0,
            skuList: promo.skuList.join(", "),
            startDate: promo.startDate ? promo.startDate.slice(0, 10) : "",
            endDate: promo.endDate ? promo.endDate.slice(0, 10) : "",
        });
        setShowModal(true);
    };

    const handleSubmit = async () => {
        try {
            const payload = {
                ...formData,
                skuList: formData.skuList.split(",").map((s) => s.trim()),
            };
            await api.post("/admin/promotions", payload);
            fetchPromotions();
            setShowModal(false);
        } catch (err) {
            alert("Failed to create promotion: " + (err.response?.data?.error || err.message));
        }
    };

    const handleUpdate = async () => {
        try {
            const payload = {
                id: editId,
                ...formData,
                skuList: formData.skuList.split(",").map((s) => s.trim()),
            };
            await api.put("/admin/promotions", payload);
            fetchPromotions();
            setShowModal(false);
            setEditId(null);
        } catch (err) {
            alert("Failed to update promotion: " + (err.response?.data?.error || err.message));
        }
    };

    const deletePromotion = async (id) => {
        try {
            await api.delete("/admin/promotions", {data: {id}});
            fetchPromotions();
        } catch (err) {
            alert("Failed to delete promotion: " + (err.response?.data?.error || err.message));
        }
    };

    if (loading) return <p className="p-4">Loading promotions…</p>;
    if (error) return <p className="p-4 text-red-600">Error: {error}</p>;

    return (
        <div className="p-6">
            <h1 className="text-2xl font-bold mb-6">Promotions Management</h1>

            <button
                onClick={openCreateModal}
                className="mb-6 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
            >
                + Add Promotion
            </button>

            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {promotions.map((promo) => (
                    <div key={promo._id} className="border rounded-lg shadow p-4">
                        <h3 className="font-semibold text-lg">{promo.title}</h3>
                        <p className="text-sm text-gray-600">{promo.description}</p>
                        <p className="text-sm mt-1">Type: {promo.type}</p>
                        <p className="text-sm">SKUs: {promo.skuList.join(", ")}</p>
                        <p className="text-sm">Active: {promo.startDate} → {promo.endDate}</p>

                        <div className="flex gap-2 mt-3">
                            <button
                                className="px-3 py-1 bg-red-500 text-white rounded hover:bg-red-600"
                                onClick={() => deletePromotion(promo._id)}
                            >
                                Delete
                            </button>
                            <button
                                className="px-3 py-1 bg-yellow-500 text-white rounded hover:bg-yellow-600"
                                onClick={() => openEditModal(promo)}
                            >
                                Edit
                            </button>
                        </div>
                    </div>
                ))}
            </div>

            {showModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white p-4 rounded-lg w-full max-w-lg h-full max-h-[90vh] overflow-y-auto shadow-lg">
                        <h2 className="text-xl font-bold">{editId ? "Edit Promotion" : "Create Promotion"}</h2>
                        <div className="mb-4">
                            <label className="block mb-1 font-medium">Title</label>
                            <input
                                className="border p-2 rounded w-full"
                                name="title"
                                placeholder="Promotion Name"
                                value={formData.title}
                                onChange={handleChange}
                            />
                        </div>

                        <div className="mb-4">
                            <label className="block mb-1 font-medium">Description</label>
                            <input
                                className="border p-2 rounded w-full"
                                name="description"
                                placeholder="Description"
                                value={formData.description}
                                onChange={handleChange}
                            />
                        </div>

                        <div className="mb-4">
                            <label className="block mb-1 font-medium">Promotion Type</label>
                            <select
                                className="border p-2 rounded w-full"
                                name="type"
                                value={formData.type}
                                onChange={handleChange}
                            >
                                <option value="BOGO_PERCENT">BOGO_PERCENT</option>
                                <option value="BOGO_FREE">BOGO_FREE</option>
                                <option value="PERCENT_OFF">PERCENT_OFF</option>
                                {/*<option value="FIXED_AMOUNT_OFF">FIXED_AMOUNT_OFF</option>*/}
                                {/*<option value="QUANTITY_DISCOUNT">QUANTITY_DISCOUNT</option>*/}
                            </select>
                        </div>

                        <div className="mb-4">
                            <label className="block mb-1 font-medium">Buy Quantity</label>
                            <input
                                type="number"
                                step="1"
                                className="border p-2 rounded w-full"
                                name="buyQuantity"
                                placeholder="Buy Quantity"
                                value={formData.buyQuantity}
                                onChange={handleChange}
                            />
                        </div>

                        <div className="mb-4">
                            <label className="block mb-1 font-medium">Get Quantity</label>
                            <input
                                type="number"
                                step="1"
                                className="border p-2 rounded w-full"
                                name="getQuantity"
                                placeholder="Get Quantity"
                                value={formData.getQuantity}
                                onChange={handleChange}
                            />
                        </div>

                        <div className="mb-4">
                            <label className="block mb-1 font-medium">Discount Percent</label>
                            <input
                                type="number"
                                step="1"
                                className="border p-2 rounded w-full"
                                name="discountPercent"
                                placeholder="Discount Percent"
                                value={formData.discountPercent}
                                onChange={handleChange}
                            />
                        </div>

                        <div className="mb-4">
                            <label className="block mb-1 font-medium">Max Discount (optional)</label>
                            <input
                                type="number"
                                step="1"
                                className="border p-2 rounded w-full"
                                name="maxDiscount"
                                placeholder="Max Discount"
                                value={formData.maxDiscount}
                                onChange={handleChange}
                            />
                        </div>

                        <div className="mb-4">
                            <label className="block mb-1 font-medium">Applicable SKUs</label>
                            <input
                                className="border p-2 rounded w-full"
                                name="skuList"
                                placeholder="Comma separated SKUs"
                                value={formData.skuList}
                                onChange={handleChange}
                            />
                        </div>


                        <div className="grid grid-cols-2 gap-4">
                            <input
                                type="date"
                                className="border p-2 rounded w-full"
                                name="startDate"
                                value={formData.startDate}
                                onChange={handleChange}
                            />
                            <input
                                type="date"
                                className="border p-2 rounded w-full"
                                name="endDate"
                                value={formData.endDate}
                                onChange={handleChange}
                            />
                        </div>

                        <div className="flex justify-end gap-3 pt-4">
                            <button
                                className="px-4 py-3 bg-gray-300 rounded hover:bg-gray-400"
                                onClick={() => setShowModal(false)}
                            >
                                Cancel
                            </button>

                            <button
                                className="px-4 py-3 bg-blue-500 text-white rounded hover:bg-blue-600"
                                onClick={() => (editId ? handleUpdate() : handleSubmit())}
                            >
                                {editId ? "Update Promotion" : "Create Promotion"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default PromotionsAdminPage;
